import Stepper from "@/components/common/Stepper";
import { formComponents as fc } from "@/components/form";
import { ui } from "@/components/ui";
import { useSelectForms } from "@/features/forms/api/selectForms";
import InjectionDetailsFormFields from "@/features/injection/components/InjectionDetailsFormFields";
import InjectionFormFields from "@/features/injection/components/InjectionFormFields";
import { SelectOrganisation } from "@/features/templates/features/select-organisation/SelectOrganisation";
import { useSchedulesQuery } from "@/features/schedules/api/selectSchedules";
import {
	type TemplateSettingsSchemaT,
	templateSettingsSchema,
} from "@/types/zod/quickstart-settings";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, X } from "lucide-react";
import { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import type {
	EventT,
	NewDiaryFormSchemaT,
	NewDiaryFormStepT,
	TrackingType,
} from "../types/newDiaryForm";
const initialSteps: NewDiaryFormStepT[] = [
	{
		stage: "current",
		words: "Administrative",
	},
	{
		stage: "future",
		words: "Trackers",
	},
	{
		stage: "future",
		words: "Events",
	},
];

interface Props {
	defaultValues?: string;
	onSuccess?: (data: NewDiaryFormSchemaT) => void;
}

export default function NewDiaryForm({ defaultValues, onSuccess }: Props) {
	const [currentStep, setCurrentStep] = useState(0);
	const [selectedEventType, setSelectedEventType] =
		useState<TrackingType | null>(null);
	const [steps, setSteps] = useState<NewDiaryFormStepT[]>(initialSteps);

	const { data: forms = [] } = useSelectForms();
	const { data: schedules = [] } = useSchedulesQuery();

	const form = useForm<TemplateSettingsSchemaT>({
		resolver: zodResolver(templateSettingsSchema),
		defaultValues: {
			trackers: [],
			...(defaultValues ? JSON.parse(defaultValues) : {}),
		},
	});

	const {
		fields: trackerFields,
		append: appendTracker,
		remove: removeTracker,
	} = useFieldArray({
		control: form.control,
		name: "trackers",
	});

	const {
		fields: eventFields,
		append: appendEvent,
		remove: removeEvent,
	} = useFieldArray({
		control: form.control,
		name: "events",
	});

	const onSubmit = (data: NewDiaryFormSchemaT) => {
		console.log(data);
		if (onSuccess) {
			onSuccess(data);
		}
	};

	const handleNext = () => {
		if (currentStep < steps.length - 1) {
			setCurrentStep(currentStep + 1);
			const newSteps = steps.map((step, index) => ({
				...step,
				stage:
					index === currentStep + 1
						? "current"
						: index < currentStep + 1
							? "past"
							: "future",
			})) as NewDiaryFormStepT[];
			setSteps(newSteps);
		}
	};

	const handleBack = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
			const newSteps = steps.map((step, index) => ({
				...step,
				stage:
					index === currentStep - 1
						? "current"
						: index < currentStep - 1
							? "past"
							: "future",
			})) as NewDiaryFormStepT[];
			setSteps(newSteps);
		}
	};

	const handleAddTracker = () => {
		appendTracker({ form_id: "", schedule_id: "" });
	};

	const handleAddEvent = (type: TrackingType) => {
		setSelectedEventType(type);
		appendEvent({ type } as EventT);
	};

	const renderStep = () => {
		switch (currentStep) {
			case 0:
				return (
					<div className="space-y-4">
						<SelectOrganisation
							onValueChange={(value: string) =>
								form.setValue("organisation_id", value)
							}
							organisations={[]} // TODO: Add organisation options
						/>
						<fc.ComboBox
							name="contact_id"
							label="Contact Person"
							options={[]} // TODO: Add contact options
						/>
						<fc.Switch
							name="has_consent"
							label="I have obtained consent from the patient"
						/>
					</div>
				);
			case 1:
				return (
					<div className="space-y-4">
						{trackerFields.map((field, index) => (
							<div key={field.id} className="flex items-start space-x-2">
								<div className="flex-1 space-y-2">
									<fc.ComboBox
										name={`trackers.${index}.form_id`}
										label="Form"
										options={forms.map((form) => ({
											value: form.form_id,
											label: form.form_name,
										}))}
									/>
									<fc.ComboBox
										name={`trackers.${index}.schedule_id`}
										label="Schedule"
										options={schedules.map((schedule) => ({
											value: schedule.schedule_id,
											label: schedule.schedule_name,
										}))}
									/>
								</div>
								<ui.Button
									type="button"
									variant="ghost"
									size="icon"
									onClick={() => removeTracker(index)}
								>
									<X className="w-4 h-4" />
								</ui.Button>
							</div>
						))}
						<ui.Button
							type="button"
							variant="outline"
							onClick={handleAddTracker}
							className="w-full"
						>
							<Plus className="mr-2 w-4 h-4" />
							Add Tracker
						</ui.Button>
					</div>
				);
			case 2:
				return (
					<div className="space-y-4">
						{eventFields.map((field, index) => (
							<div key={field.id} className="flex items-start space-x-2">
								<div className="flex-1">
									{field.type === "injection" && (
										<div className="space-y-4">
											<InjectionFormFields
												organisationId={form.watch("organisation_id")}
											/>
											<InjectionDetailsFormFields injectionId={field.id} />
										</div>
									)}
									{/* Add other event type forms here */}
								</div>
								<ui.Button
									type="button"
									variant="ghost"
									size="icon"
									onClick={() => removeEvent(index)}
								>
									<X className="w-4 h-4" />
								</ui.Button>
							</div>
						))}
						<div className="gap-4 grid grid-cols-2">
							<ui.Button
								type="button"
								variant="outline"
								onClick={() => handleAddEvent("injection")}
							>
								Add Injection
							</ui.Button>
							<ui.Button
								type="button"
								variant="outline"
								onClick={() => handleAddEvent("medication")}
							>
								Add Medication
							</ui.Button>
							<ui.Button
								type="button"
								variant="outline"
								onClick={() => handleAddEvent("surgery")}
							>
								Add Surgery
							</ui.Button>
							<ui.Button
								type="button"
								variant="outline"
								onClick={() => handleAddEvent("product")}
							>
								Add Product
							</ui.Button>
							<ui.Button
								type="button"
								variant="outline"
								onClick={() => handleAddEvent("shockwave")}
							>
								Add Shockwave
							</ui.Button>
						</div>
					</div>
				);
			default:
				return null;
		}
	};

	return (
		<ui.Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
				<Stepper steps={steps} />
				{renderStep()}
				<div className="flex justify-between">
					<ui.Button
						type="button"
						variant="outline"
						onClick={handleBack}
						disabled={currentStep === 0}
					>
						Back
					</ui.Button>
					{currentStep === steps.length - 1 ? (
						<ui.Button type="submit">Save Template</ui.Button>
					) : (
						<ui.Button type="button" onClick={handleNext}>
							Next
						</ui.Button>
					)}
				</div>
			</form>
		</ui.Form>
	);
}
